import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, MapPin, Clock, Ticket, Gift, ChevronDown, ChevronUp, CheckCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';
import PhotoLightbox from '../components/PhotoLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EventPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noEvent, setNoEvent] = useState(false);

  // RSVP form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [ticketQty, setTicketQty] = useState(1);
  const [selectedItem, setSelectedItem] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rsvpSuccess, setRsvpSuccess] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  // Phase D — click any thumbnail to open the unified PhotoLightbox.
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const fetchEvent = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/events/active`);
      if (res.data.active) {
        setEvent(res.data.event);
      } else {
        setNoEvent(true);
        setTimeout(() => navigate('/about'), 2000);
      }
    } catch {
      setNoEvent(true);
      setTimeout(() => navigate('/about'), 2000);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  useEffect(() => {
    if (searchParams.get('rsvp') === 'success') {
      setRsvpSuccess(true);
    }
  }, [searchParams]);

  const handleRsvp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/public/events/${event.id}/rsvp`, {
        name, email, ticket_quantity: ticketQty, selected_donation_item: selectedItem
      });
      if (res.data.type === 'paid' && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setRsvpSuccess(true);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="w-8 h-8 border-2 border-[#BA1826] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (noEvent) {
    return (
      <div className="py-24 text-center px-6">
        <p className="text-[#4B5563] text-sm">No active event right now. Redirecting to About Us...</p>
      </div>
    );
  }

  if (rsvpSuccess) {
    return (
      <div className="py-16 px-6">
        <div className="max-w-md mx-auto text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-3">You're Registered!</h2>
          <p className="text-[#4B5563] text-sm mb-6">
            {event?.confirmation_message || 'Thank you for registering! We look forward to seeing you.'}
          </p>
          <button onClick={() => navigate('/')} className="btn-primary text-sm" data-testid="rsvp-home-btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const isPaid = event.ticket_type === 'paid' && event.ticket_price > 0;

  return (
    <div>
      {/* Hero with Flyer */}
      <section className="bg-[#0A0A0A] py-10" data-testid="event-hero">
        <div className="px-6 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-2">
            Upcoming Event
          </p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4" data-testid="event-title">
            {event.title}
          </h1>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-300">
            {event.date && (
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-[#BA1826]" /> {event.date}
              </span>
            )}
            {event.time && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-[#BA1826]" /> {event.time}
              </span>
            )}
            {event.location && (
              <span className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-[#BA1826]" /> {event.location}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Flyer Image */}
      {event.flyer_image && (
        <section className="bg-white py-8" data-testid="event-flyer">
          <div className="px-6 max-w-lg mx-auto">
            <img src={event.flyer_image} alt={event.title} className="w-full rounded-lg shadow-lg" />
          </div>
        </section>
      )}

      {/* Event Description */}
      {event.description && (
        <section className="py-8 bg-white" data-testid="event-description">
          <div className="px-6 max-w-xl mx-auto">
            <h2 className="font-heading text-lg font-bold text-[#0A0A0A] mb-3">About This Event</h2>
            <p className="text-sm text-[#4B5563] leading-relaxed whitespace-pre-line">{event.description}</p>
          </div>
        </section>
      )}

      {/* Ticket / Price Info */}
      <section className="py-6 bg-[#F8F9FA] border-y border-[#E5E7EB]" data-testid="event-ticket-info">
        <div className="px-6 max-w-xl mx-auto text-center">
          <Ticket className="w-6 h-6 text-[#BA1826] mx-auto mb-2" />
          {isPaid ? (
            <>
              <p className="text-2xl font-bold text-[#BA1826]">${event.ticket_price.toFixed(2)}</p>
              <p className="text-xs text-[#4B5563]">per ticket</p>
            </>
          ) : (
            <>
              <p className="text-xl font-bold text-[#0A0A0A]">Free Event</p>
              <p className="text-xs text-[#4B5563]">RSVP to reserve your spot</p>
            </>
          )}
        </div>
      </section>

      {/* Donation Items List */}
      {event.donation_items && event.donation_items.length > 0 && (
        <section className="py-8 bg-white" data-testid="donation-items-section">
          <div className="px-6 max-w-xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <Gift className="w-5 h-5 text-[#BA1826]" />
              <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">Donation Items Welcome</h2>
            </div>
            <p className="text-xs text-[#4B5563] mb-4">
              Bring any of the following items to support families in need:
            </p>
            <div className="space-y-2">
              {event.donation_items.map((group) => (
                <div key={group.category} className="border border-[#E5E7EB] rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedCategory(expandedCategory === group.category ? null : group.category)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-[#F8F9FA] hover:bg-[#F1F2F4] transition-colors text-left"
                    data-testid={`donation-category-${group.category.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <span className="text-sm font-semibold text-[#0A0A0A]">{group.category}</span>
                    {expandedCategory === group.category ? <ChevronUp className="w-4 h-4 text-[#4B5563]" /> : <ChevronDown className="w-4 h-4 text-[#4B5563]" />}
                  </button>
                  {expandedCategory === group.category && (
                    <div className="px-4 py-3 space-y-1.5">
                      {group.items.map((item) => (
                        <label key={item} className="flex items-center gap-2 text-sm text-[#4B5563] cursor-pointer">
                          <input
                            type="radio"
                            name="donation_item"
                            value={item}
                            checked={selectedItem === item}
                            onChange={() => setSelectedItem(item)}
                            className="text-[#BA1826] focus:ring-[#BA1826]"
                          />
                          {item}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {selectedItem && (
              <p className="mt-3 text-xs text-[#BA1826] font-medium">
                You plan to bring: {selectedItem}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Event Photos */}
      {event.photos && event.photos.length > 0 && (
        <section className="py-8 bg-[#F8F9FA]" data-testid="event-photos">
          <div className="px-6 max-w-xl mx-auto">
            <div className="grid grid-cols-2 gap-3">
              {event.photos.map((photo, idx) => (
                <button key={photo} onClick={() => setLightboxIdx(idx)}
                  className="w-full rounded-lg overflow-hidden aspect-square hover:opacity-90 transition-opacity"
                  data-testid={`event-photo-${idx}`}>
                  <img src={photo} alt="Event photo" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
          {/* Phase D: unified lightbox — object-contain, ESC/backdrop
              close, mobile-safe height, prev/next through the story. */}
          <PhotoLightbox
            photos={event.photos}
            open={lightboxIdx !== null}
            startIndex={lightboxIdx || 0}
            title={event.title}
            onClose={() => setLightboxIdx(null)}
          />
        </section>
      )}

      {/* RSVP Form */}
      {event.rsvp_enabled && (
        <section className="py-10 bg-white" data-testid="rsvp-section">
          <div className="px-6 max-w-xl mx-auto">
            <h2 className="font-heading text-lg font-bold text-[#0A0A0A] mb-1 text-center">
              {isPaid ? 'Get Your Tickets' : 'RSVP Now'}
            </h2>
            <p className="text-xs text-[#4B5563] text-center mb-6">
              Reserve your spot for this event.
            </p>

            <form onSubmit={handleRsvp} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                  placeholder="Your name"
                  data-testid="rsvp-name"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                  placeholder="your@email.com"
                  data-testid="rsvp-email"
                />
              </div>
              {isPaid && (
                <div>
                  <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Number of Tickets</label>
                  <select
                    value={ticketQty}
                    onChange={(e) => setTicketQty(parseInt(e.target.value))}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                    data-testid="rsvp-qty"
                  >
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <option key={n} value={n}>{n} {n === 1 ? 'ticket' : 'tickets'} — ${(event.ticket_price * n).toFixed(2)}</option>
                    ))}
                  </select>
                </div>
              )}
              <button
                type="submit"
                disabled={submitting || !name || !email}
                className="btn-primary w-full text-sm disabled:opacity-50"
                data-testid="rsvp-submit-btn"
              >
                {(() => {
                  if (submitting) return 'Processing...';
                  if (isPaid) return `Pay $${(event.ticket_price * ticketQty).toFixed(2)} & Register`;
                  return 'Confirm RSVP';
                })()}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </section>
      )}
    </div>
  );
};

export default EventPage;
